
import { useLanguage } from "@/contexts/LanguageContext";
import { ChevronDown, ChevronUp, Info } from "lucide-react";
import { useState } from "react";
import { formatNumber } from "./utils";
import { PerformanceChart } from "./PerformanceChart";

interface PerformanceMetricsProps {
  totalImpressions: number;
  expectedClicks: number;
  totalPlaytimes: number;
  impressionsData: any[];
  clicksData: any[];
  playsData: any[];
  hasTableAdsLocations: boolean; // New prop to check if there are TableAds locations
}

const PerformanceMetrics = ({
  totalImpressions,
  expectedClicks,
  totalPlaytimes,
  impressionsData,
  clicksData,
  playsData,
  hasTableAdsLocations
}: PerformanceMetricsProps) => {
  const { language } = useLanguage();
  const [isPerformanceExpanded, setIsPerformanceExpanded] = useState(false);

  // If there are no TableAds locations, don't render anything
  if (!hasTableAdsLocations) {
    return null;
  }

  return (
    <div className="pt-4 border-t border-border">
      <div>
        <h4 className={`${language === 'ar' ? 'text-xl' : 'text-md'} font-semibold text-foreground mb-2`}>
          {language === 'ar' ? 'الأداء المتوقع' : 'Estimated Performance'}
        </h4>
        
        {/* Add the tip about TableAds locations */}
        <div className="flex items-start gap-2 bg-accent/50 dark:bg-accent/20 p-3 rounded-md mb-3 text-sm text-muted-foreground">
          <Info size={18} className="text-primary mt-0.5 flex-shrink-0" />
          <p>
            {language === 'ar' 
              ? 'هذه التقديرات تنطبق فقط على مواقع TableAds وقد تختلف الأرقام الفعلية'
              : 'These performance estimations only apply to TableAds locations and actual numbers may vary'}
          </p>
        </div>
        
        <button 
          onClick={() => setIsPerformanceExpanded(!isPerformanceExpanded)}
          className="w-full flex items-center justify-center gap-2 py-2 px-4 bg-muted hover:bg-muted/80 dark:bg-muted/50 dark:hover:bg-muted/70 rounded-lg transition-colors mb-4"
        >
          <span className="text-sm text-muted-foreground">
            {language === 'ar' 
              ? (isPerformanceExpanded ? 'إخفاء الرسوم البيانية' : 'عرض الرسوم البيانية')
              : (isPerformanceExpanded ? 'Hide Graphs' : 'Show Graphs')
            }
          </span>
          {isPerformanceExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </button>
      </div>

      <div className="space-y-2">
        <div className={`flex justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <span className={`text-muted-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
            {language === 'ar' ? ':إجمالي المشاهدات' : 'Total Impressions:'}
          </span>
          <span className={`font-medium text-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
            {formatNumber(totalImpressions)}
          </span>
        </div>
        
        <div className={`flex justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <span className={`text-muted-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
            {language === 'ar' ? ':التفاعل المتوقع' : 'Expected Engagement:'}
          </span>
          <span className={`font-medium text-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
            {formatNumber(expectedClicks)} {language === 'ar' ? 'نقرة' : 'clicks'}
          </span>
        </div>

        <div className={`flex justify-between ${language === 'ar' ? 'flex-row-reverse' : ''}`}>
          <span className={`text-muted-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
            {language === 'ar' ? ':عدد مرات تكرار الإعلان' : 'Advertisement Playtimes:'}
          </span>
          <span className={`font-medium text-foreground ${language === 'ar' ? 'text-lg' : 'text-base'}`}>
            {formatNumber(totalPlaytimes)} {language === 'ar' ? 'مرة' : 'plays'}
          </span>
        </div>
      </div>
      
      {isPerformanceExpanded && (
        <div className="mt-6 space-y-4">
          <PerformanceChart 
            data={impressionsData}
            dataKey="impressions"
            color="#4F46E5"
            title={language === 'ar' ? 'المشاهدات' : 'Impressions'}
          />
          <PerformanceChart 
            data={clicksData}
            dataKey="clicks"
            color="#10B981"
            title={language === 'ar' ? 'النقرات' : 'Clicks'}
          />
          <PerformanceChart 
            data={playsData}
            dataKey="plays"
            color="#F59E0B"
            title={language === 'ar' ? 'التكرار' : 'Plays'}
          />
        </div>
      )}
    </div>
  );
};

export default PerformanceMetrics;
